import {
  Box,
  Chip,
  FormHelperText,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import { OrganizationUserContext } from "contexts/Organization";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { unstable_usePrompt } from "react-router-dom";
import { OrganizationNiche } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import Editor from "components/Editor";
import MergeTagMenuBar from "components/Editor/MergeTag/MergeTagMenuBar";
import { useMergeTags } from "components/Editor/MergeTag/useMergeTags";
import { extensions } from "components/Editor/utils";
import StyledTextField from "components/Styled/TextField";
import { MAX_NUMBER_OF_FOLLOWUPS } from "constants/influencer";
import { MERGE_TAGS } from "constants/templates";
import AttachmentManager from "features/Influencer/ContactList/AttachmentManager";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { NichesPill } from "features/Influencer/Templates/TemplatesTable/EditTemplate/components/HeaderControls";
import { isPage } from "utils/general";
import { unwrapHighlightContent } from "utils/string";
import { trackEvent } from "utils/tracking";
import { useBulkSend } from "utils/useBulkSend";
import useTemplate from "utils/useTemplate";

const styles = {
  errorContent: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      "&:hover fieldset": {
        borderColor: "red",
      },
      "& fieldset": {
        borderColor: "red",
      },
    },
  },
  errorText: { color: "red" },
};

interface TemplateBodyProps {
  editTemplate: Template;
  setEditTemplate: Dispatch<SetStateAction<Template>>;
  setIsDirty?: Dispatch<SetStateAction<boolean>>;
  isNew?: boolean;
  allowChangeType?: boolean;
  allowChangeTitle?: boolean;
  displayError?: boolean;
  isDirty?: boolean;
  minimalist?: boolean;
  fromAdmin?: boolean;
  // Bumped by the parent when the template body/subject is being replaced from
  // outside the editor (e.g. restoring a past rubric revision). The TipTap
  // editors only honor their initial `content` prop, so we need an explicit
  // signal to call `setContent` on them.
  restoreSignal?: number;
}

export default function TemplateBody({
  editTemplate,
  setEditTemplate,
  setIsDirty,
  isNew = false,
  displayError,
  isDirty,
  allowChangeTitle = true,
  minimalist = false,
  fromAdmin = false,
  restoreSignal,
}: TemplateBodyProps) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { currentOrg, organizationVaProfile } = useContext(
    OrganizationUserContext,
  );
  const [attachmentDialogOpen, setAttachmentDialogOpen] =
    useState<boolean>(false);
  const { getUnfilledVariables } = useTemplate();
  const { fetchNiches } = useBulkSend();

  const [toggleMergeTagTooltip, toggleVariableTooltip] = useMergeTags();

  const [deletedMergeTag, setDeletedMergeTag] = useState("");
  const [unfilledVariables, setUnfilledVariables] = useState<string[]>([]);
  const [hasApplyHighlighted, setHasApplyHighlighted] = useState(false);

  const editNiche =
    currentOrg?.isVaUser &&
    !isPage(routes.onboardingVaSelfServe) &&
    !isPage(routes.virtualAssistant);

  const displayMergeTagWarning = (deletedMergeTag: string) => {
    setDeletedMergeTag(deletedMergeTag);
  };
  const hasHighlightedContent = editTemplate?.body?.includes("<mark") ?? false;

  const subjectEditor = useEditor(
    {
      extensions: extensions({
        isPlain: true,
        suggestionOptions: MERGE_TAGS,
        placeholderText: "Email Subject",
      }),
      content: editTemplate?.subject,
      onUpdate({ editor }) {
        setEditTemplate((prevTemplate: Template) => {
          return { ...prevTemplate, subject: editor.getHTML() };
        });

        if (setIsDirty) setIsDirty(true);
      },
    },
    [],
  ) as TipTapEditor;

  const editor = useEditor(
    {
      extensions: extensions({
        toggleVariableTooltip,
        toggleMergeTagTooltip,
        suggestionOptions: MERGE_TAGS,
        displayMergeTagWarning,
        placeholderText: "Email Body",
      }),
      content: editTemplate?.body,
      onUpdate({ editor }) {
        if (setIsDirty) setIsDirty(true);
        if (hasHighlightedContent) {
          const isInsideHighlight = editor.isActive("highlight");
          // If user edit highlight content, unhighlight it.
          if (isInsideHighlight) {
            const { from, to } = editor.state.selection;
            const cleanedHTML = unwrapHighlightContent(editor.getHTML() || "");
            editor.commands.setContent(cleanedHTML);
            // Put back cursor in right place where user was last there
            editor.chain().focus().setTextSelection({ from, to }).run();
          }
        }
        setEditTemplate((prevTemplate: Template) => {
          return { ...prevTemplate, body: editor.getHTML() };
        });
      },
    },
    // There is an issue here for all theses editors between new and loading template
    [isNew === true || !!editTemplate.body || editTemplate?.contentType],
  ) as TipTapEditor;

  const handleTemplateChange = (e: CustomEvent) => {
    const { name, value } = e.target;
    setEditTemplate((prevTemplate: Template) => {
      return { ...prevTemplate, [name]: value };
    });

    if (setIsDirty) setIsDirty(true);
  };

  useEffect(() => {
    if (editor) {
      editor.commands.setContent(editTemplate?.body || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTemplate?.contentType]);

  useEffect(() => {
    if (
      editor &&
      hasHighlightedContent &&
      !editor.getHTML()?.includes("<mark") &&
      !hasApplyHighlighted
    ) {
      editor.commands.setContent(editTemplate?.body || "");
      setHasApplyHighlighted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHighlightedContent]);

  useEffect(() => {
    const errors = getUnfilledVariables(editTemplate?.body || "");
    setUnfilledVariables(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTemplate?.body]);

  useEffect(() => {
    if (subjectEditor && editTemplate?.id && editTemplate?.subject) {
      subjectEditor.commands.setContent(editTemplate?.subject || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTemplate?.id]);

  useEffect(() => {
    if (!restoreSignal) return;
    if (editor) {
      editor.commands.setContent(editTemplate?.body || "");
    }
    if (subjectEditor) {
      subjectEditor.commands.setContent(editTemplate?.subject || "");
    }
    if (setIsDirty) setIsDirty(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreSignal]);

  useEffect(() => {
    if (editNiche && currentOrg?.id) {
      if (
        !organizationVaProfile?.niches ||
        organizationVaProfile?.niches?.length === 0
      )
        fetchNiches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editNiche, currentOrg?.id]);

  const hasFirstNameTag =
    editor && editor?.getHTML()?.includes('data-label="First Name"');

  const hasBrandNameTag =
    editor && editor?.getHTML()?.includes('data-label="Brand Name"');

  const hasRedBorder = !editTemplate?.name && displayError;

  unstable_usePrompt({
    message:
      "You have an unsaved template. Are you sure you want to navigate away from this page?",
    when: ({ currentLocation, nextLocation }) =>
      Boolean(isDirty) && currentLocation.pathname !== nextLocation.pathname,
  });

  const renderVariable = (label: string) => {
    return (
      <Box
        component="span"
        sx={{
          p: 0.5,
          mx: 0.5,
          backgroundColor: "#fce6df",
          color: "black",
          borderRadius: 2,
        }}
        key={label}
      >
        {label}
      </Box>
    );
  };

  const chooseType = (type: TemplateContentType) => {
    trackEvent("Create Template Dialog Form - Select Template Type", {
      "Template Type": type,
    });
    setEditTemplate((prevTemplate: Template) => {
      return { ...prevTemplate, contentType: type };
    });

    if (setIsDirty) setIsDirty(true);
  };

  const displayedNiches = () => {
    // At the moment, VA user can only set a followup on a Template
    // if it has an outreach, or there are still slots to add another follow-up.
    if (editTemplate?.contentType === TemplateContentType.FOLLOWUP) {
      return (
        organizationVaProfile?.niches?.filter((niche) => {
          return (
            (niche?.templates &&
              niche?.templates?.length > 0 &&
              niche?.templates?.length < MAX_NUMBER_OF_FOLLOWUPS + 1) ||
            niche?.templates?.some((x) => x.id === editTemplate.id)
          );
        }) || []
      );
    } else {
      return organizationVaProfile?.niches || [];
    }
  };

  const selectableNiches = displayedNiches();
  const sortedNiches = [...selectableNiches].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? ""),
  );

  const handleSelectNiches = (nicheIds: number[]) => {
    const selectedNiches = nicheIds
      .map((id) =>
        organizationVaProfile?.niches?.find((niche) => niche.id === id),
      )
      .filter((niche): niche is OrganizationNiche => Boolean(niche));

    setEditTemplate((prevTemplate: Template) => {
      return {
        ...prevTemplate,
        niches: selectedNiches,
        nicheIds: selectedNiches.map((niche) => niche.id!),
        updateMultiNiches: true,
      };
    });

    if (setIsDirty) setIsDirty(true);
  };

  return (
    <>
      <Grid container gap={1}>
        {fromAdmin && (
          <Grid item xs={12} sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Template type:
              </Typography>
              {[
                { value: TemplateContentType.OUTREACH, label: "Outreach" },
                { value: TemplateContentType.FOLLOWUP, label: "Followup" },
              ].map(({ value, label }) => {
                const selected = editTemplate?.contentType === value;
                return (
                  <Chip
                    key={value}
                    label={label}
                    size="small"
                    clickable
                    onClick={() => chooseType(value)}
                    variant={selected ? "filled" : "outlined"}
                    color={selected ? "primary" : "default"}
                  />
                );
              })}
            </Box>
          </Grid>
        )}

        {editNiche && selectableNiches?.length > 0 && (
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <NichesPill
                niches={sortedNiches}
                selectedIds={
                  editTemplate?.niches?.map((niche) => niche.id as number) ?? []
                }
                onChange={handleSelectNiches}
              />
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.625,
                  px: 1,
                  py: 0.375,
                  borderRadius: 999,
                  bgcolor: grey[100],
                  color: grey[600],
                  fontSize: 11.5,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                <Box
                  component="i"
                  className="fa-regular fa-clock"
                  sx={{ fontSize: 11 }}
                />
                7 days to take effect
              </Box>
            </Box>
          </Grid>
        )}

        {allowChangeTitle && (
          <Grid item xs={12} sx={{ mb: 2, mt: -0.5 }}>
            {hasRedBorder && (
              <Typography variant="body2" sx={styles.errorText}>
                Please fill in the template name
              </Typography>
            )}
            <StyledTextField
              required
              value={editTemplate?.name}
              onChange={handleTemplateChange}
              fullWidth
              label="Template Name"
              placeholder="Template Name"
              name="name"
              styles={hasRedBorder ? styles.errorContent : {}}
            />
          </Grid>
        )}

        {editTemplate?.contentType !== TemplateContentType.FOLLOWUP && (
          <Grid item xs={12} sx={{ mt: -0.5 }}>
            <MergeTagMenuBar
              editor={subjectEditor}
              mergeTags={MERGE_TAGS}
              isSubjectLine
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 1,
                px: 1.75,
                border: "1px solid",
                borderColor: "purple.light",
                borderRadius: 4,
                bgcolor: "#fff",
              }}
            >
              {isMobileScreen ? (
                <></>
              ) : (
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    color: "secondary.dark",
                    whiteSpace: "nowrap",
                  }}
                >
                  Subject
                </Box>
              )}

              <Editor
                editor={subjectEditor}
                hideMenu
                hasBorder={false}
                mergeTags={MERGE_TAGS}
              />
            </Box>
          </Grid>
        )}

        <Grid
          item
          xs={12}
          sx={{
            mt:
              editTemplate?.contentType === TemplateContentType.FOLLOWUP
                ? -0.5
                : 2,
          }}
        >
          <Box sx={{ mb: 1 }}>
            <MergeTagMenuBar editor={editor} mergeTags={MERGE_TAGS} />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              border: "1px solid",
              borderColor: "purple.light",
              borderRadius: 4,
              bgcolor: "#fff",
            }}
          >
            <Editor
              editor={editor}
              allowImage={true}
              allowAttachment={true}
              openAttachmentManager={() => {
                setAttachmentDialogOpen(true);
              }}
              attachment={editTemplate?.attachment}
              removeAttachment={() => {
                setEditTemplate((prevTemplate: Template) => {
                  return { ...prevTemplate, attachment: undefined };
                });
                if (setIsDirty) setIsDirty(true);
              }}
              hasBorder={false}
            />
          </Box>
        </Grid>

        <FormHelperText>
          {unfilledVariables?.length > 0 && (
            <>
              Please fill out {unfilledVariables?.map((x) => renderVariable(x))}
            </>
          )}
          {deletedMergeTag === "Brand Name" && !hasBrandNameTag && (
            <>
              Keep{" "}
              <Box
                component="span"
                sx={{
                  backgroundColor: grey[100],
                  p: 0.5,
                  borderRadius: 2,
                  mx: 0.5,
                }}
              >
                Brand Name
              </Box>{" "}
              tag so Bento can customize each outreach emails to the Brand's
              name for you.
            </>
          )}
          {deletedMergeTag === "First Name" && !hasFirstNameTag && (
            <>
              Keep{" "}
              <Box
                component="span"
                sx={{
                  backgroundColor: grey[100],
                  p: 0.5,
                  borderRadius: 2,
                  mx: 0.5,
                }}
              >
                First Name{" "}
              </Box>{" "}
              tag so Bento can customize each outreach emails to the recipient's
              first name for you.
            </>
          )}
        </FormHelperText>
      </Grid>
      <AttachmentManager
        enteredSelection={(attachment: string) => {
          setEditTemplate((prevTemplate: Template) => {
            return { ...prevTemplate, attachment };
          });
          if (setIsDirty) setIsDirty(true);
        }}
        open={attachmentDialogOpen}
        setOpen={setAttachmentDialogOpen}
      />
    </>
  );
}
